import { useEffect, useMemo } from 'react';

import {
  extractFirstNumber,
  getServiceKeyByType
} from '@/components/AdvertisePayments/AdvertisePayments.helpers';
import { UseServicePeriodOptionsParams } from '@/components/AdvertisePayments/hooks/types';
import { SERVICE_KEY_BY_TYPE } from '@/constants/common';

import { useMapServicePeriodItems } from './useMapServicePeriodItems';

const MIN_PERIOD_ITEMS = 3;

export const useServicePeriodOptions = ({
  paidServices,
  serviceType,
  activePeriodId,
  setActivePeriodId,
  setSelectedServiceId
}: UseServicePeriodOptionsParams) => {
  const { mapTimes, mapDays } = useMapServicePeriodItems();

  const serviceKey = useMemo(
    () => getServiceKeyByType(serviceType),
    [serviceType]
  );

  const currentService = useMemo(() => {
    if (serviceKey === null) return undefined;
    return paidServices.find(service => service.id === serviceKey);
  }, [paidServices, serviceKey]);

  const mappedPeriodItems = useMemo(() => {
    if (!currentService || serviceKey === null) return [];

    if (serviceKey === SERVICE_KEY_BY_TYPE.BUMP)
      return mapTimes(currentService.options);

    if (serviceKey === SERVICE_KEY_BY_TYPE.FEATURED)
      return mapDays(currentService.options);

    return [];
  }, [currentService, serviceKey, mapTimes, mapDays]);

  useEffect(() => {
    if (!currentService) return;

    if (mappedPeriodItems.length >= MIN_PERIOD_ITEMS) {
      let defaultPeriodId: number | null = activePeriodId;

      if (activePeriodId === null) {
        defaultPeriodId = mappedPeriodItems[2]?.id;
        setActivePeriodId(defaultPeriodId);
      }

      const matchedOption = currentService.options.find(option => {
        const numberValue =
          extractFirstNumber(option.id) ?? extractFirstNumber(option.name);
        return numberValue === defaultPeriodId;
      });

      if (matchedOption?.id) {
        setSelectedServiceId(matchedOption.id);
      }
    }
  }, [
    currentService,
    mappedPeriodItems,
    activePeriodId,
    setActivePeriodId,
    setSelectedServiceId
  ]);

  const handleSelectPeriod = (periodId: number) => {
    if (!currentService) return;
    setActivePeriodId(periodId);

    const matchedOption = currentService.options.find(option => {
      const numberValue =
        extractFirstNumber(option.id) ?? extractFirstNumber(option.name);
      return numberValue === periodId;
    });

    if (matchedOption?.id) {
      setSelectedServiceId(matchedOption.id);
    }
  };

  return { periodItems: mappedPeriodItems, handleSelect: handleSelectPeriod };
};
